<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ConversationApi from 'dashboard/api/inbox/conversation';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

// Pick the Bukku contact for a chat that phone/email matching couldn't place.
// Opens with Smith3D's suggestions (contacts behind documents already sent in
// this chat); typing searches by name, phone, email or INV-/QUO-/DO- number.
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const emit = defineEmits(['close', 'linked']);

const SEARCH_DELAY_MS = 350;

const { t } = useI18n();
const dialogRef = ref(null);
const query = ref('');
const results = ref([]);
const isSearching = ref(false);
const linkingId = ref(null);
const errorText = ref('');
let searchTimer = null;
let searchSeq = 0;

const errorFrom = error =>
  error?.response?.data?.error || t('S3_CUSTOMER_PANEL.LINK.FAILED');

const search = async () => {
  searchSeq += 1;
  const seq = searchSeq;
  isSearching.value = true;
  errorText.value = '';
  try {
    const { data } = await ConversationApi.searchBukkuContacts(
      props.conversationId,
      query.value.trim()
    );
    if (seq === searchSeq) results.value = data.results || [];
  } catch (error) {
    if (seq === searchSeq) {
      results.value = [];
      errorText.value = errorFrom(error);
    }
  } finally {
    if (seq === searchSeq) isSearching.value = false;
  }
};

watch(query, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(search, SEARCH_DELAY_MS);
});

const link = async contact => {
  linkingId.value = contact.id;
  errorText.value = '';
  try {
    const { data } = await ConversationApi.linkBukkuContact(
      props.conversationId,
      contact.id
    );
    emit('linked', data);
    dialogRef.value.close();
  } catch (error) {
    errorText.value = errorFrom(error);
  } finally {
    linkingId.value = null;
  }
};

onMounted(() => {
  dialogRef.value.open();
  search();
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('S3_CUSTOMER_PANEL.LINK.TITLE')"
    :description="t('S3_CUSTOMER_PANEL.LINK.DESC')"
    :show-confirm-button="false"
    width="lg"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-2">
      <input
        v-model="query"
        type="search"
        class="w-full !mb-0"
        :placeholder="t('S3_CUSTOMER_PANEL.LINK.PLACEHOLDER')"
        autofocus
      />
      <p v-if="errorText" class="mb-0 text-sm text-n-ruby-11">
        {{ errorText }}
      </p>
      <p
        v-else-if="isSearching && !results.length"
        class="mb-0 text-sm text-n-slate-11"
      >
        {{ t('S3_CUSTOMER_PANEL.LINK.SEARCHING') }}
      </p>
      <p v-else-if="!results.length" class="mb-0 text-sm text-n-slate-11">
        {{ t('S3_CUSTOMER_PANEL.LINK.NO_RESULTS') }}
      </p>
      <p
        v-if="!query.trim() && results.length"
        class="mb-0 text-xs text-n-slate-11"
      >
        {{ t('S3_CUSTOMER_PANEL.LINK.SUGGESTED') }}
      </p>
      <div class="flex flex-col max-h-[50vh] overflow-y-auto">
        <button
          v-for="contact in results"
          :key="contact.id"
          type="button"
          class="flex items-center gap-2 px-2 py-2 border-b rounded-md border-n-weak text-start hover:bg-n-alpha-1 disabled:opacity-50"
          :disabled="!!linkingId"
          @click="link(contact)"
        >
          <span class="flex flex-col flex-1 min-w-0">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ contact.title }}
            </span>
            <span
              v-if="contact.subtitle"
              class="text-xs truncate text-n-slate-11"
            >
              {{ contact.subtitle }}
            </span>
          </span>
          <span class="text-xs text-n-blue-11 shrink-0">
            {{
              linkingId === contact.id
                ? t('S3_CUSTOMER_PANEL.LINK.LINKING')
                : t('S3_CUSTOMER_PANEL.LINK.PICK')
            }}
          </span>
        </button>
      </div>
    </div>
  </Dialog>
</template>
